import {
  useCart,
  useProduct,
  AddToCartButton,
  Money,
} from '@shopify/hydrogen-react'
import { useAppStore } from 'lib/hooks/useAppStore'
import { useIsMobile } from 'lib/hooks/useIsMobile'
import {
  sendShopifyAnalytics,
  getClientBrowserParameters,
  AnalyticsEventName,
} from '@shopify/hydrogen-react'
import { useEffect, useMemo } from 'react'
import { ProductShippingDate } from './ProductShippingDate'

export const ProductForm = ({
  onChangeColor,
  featuredImage = '',
  shippingDate,
  isPreorder,
  isBackorder,
  discount,
}) => {
  const { cartState, setCartState, setNotifyId } = useAppStore()
  const { status, id: cartId } = useCart()
  const isMobile = useIsMobile()
  const {
    product,
    selectedVariant,
    isOptionInStock,
    options,
    selectedOptions,
    setSelectedOption,
  } = useProduct() as any

  useEffect(() => {
    if (selectedOptions.Color && onChangeColor) {
      onChangeColor(selectedOptions.Color)
    }
  }, [])

  const addToCartText = useMemo(() => {
    if (!isPreorder && !isBackorder) return 'Add to Cart'
    const discountText = discount ? ` — ${discount} Off` : ''
    if (isPreorder) return `Preorder${discountText}`
    if (isBackorder) return `Backorder${discountText}`
  }, [isPreorder, isBackorder, discount])

  const cartAttributes = useMemo(() => {
    const attributes = [
      {
        key: 'Image',
        value: featuredImage,
      },
    ]

    if (shippingDate && (isPreorder || isBackorder)) {
      attributes.push({
        key: isPreorder ? 'Preorder' : 'Backorder',
        value: `Shipping starts ${new Date(shippingDate).toLocaleDateString(
          'en-US',
          { month: 'long', day: 'numeric' }
        )}`,
      })
    }
    return attributes
  }, [featuredImage, isPreorder, isBackorder, shippingDate])

  return (
    <div>
      {options?.map((option, index) => {
        if (option.values?.length <= 1) return null
        return (
          <div
            key={`${option.name}-${index}`}
            className="pb-20 text-11 leading-100"
          >
            <div className="pb-8 uppercase text-darkGray">{option.name}</div>
            <div className="flex gap-[5px] rounded-8 bg-white p-4">
              {option.values.map((value, i) => {
                const available = isOptionInStock(option.name, value)
                const isActive = selectedOptions[option.name] === value
                if (option.name === 'Color') {
                  const color = product.colors?.find(
                    (color) => color.label === value
                  )
                  return (
                    <button
                      tabIndex={0}
                      className={`ease relative h-[18px] w-[18px] rounded-5 border-1 transition-colors duration-300 ${
                        isActive ? 'border-[#aaa]' : 'border-[transparent]'
                      } ${!available ? 'opacity-50' : ''}`}
                      key={`value-${value}-${i}`}
                      onClick={() => {
                        onChangeColor(value)
                        setSelectedOption(option.name, value)
                      }}
                    >
                      <div
                        className="absolute left-1/2 top-1/2 h-[12px] w-[12px] -translate-x-1/2 -translate-y-1/2 rounded-3"
                        style={{
                          background: color?.value ?? 'black',
                          border: '.5px solid black',
                        }}
                      ></div>
                    </button>
                  )
                }
                return (
                  <button
                    tabIndex={0}
                    className={`ease flex-1 rounded-6 px-8 py-10 transition-colors duration-200 ${
                      isActive ? 'bg-[#DEDEDE]' : 'bg-white'
                    } hover:bg-[#DEDEDE] ${!available ? 'opacity-50' : ''}`}
                    key={`value-${value}-${i}`}
                    onClick={() => {
                      setSelectedOption(option.name, value)
                    }}
                  >
                    {value}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
      <ProductShippingDate date={shippingDate} />
      {selectedVariant?.availableForSale ? (
        <AddToCartButton
          accessibleAddingToCartLabel="Add to Cart"
          variantId={selectedVariant.id}
          attributes={cartAttributes}
          quantity={1}
          onClick={() => {
            if (isMobile) {
              return setCartState('open')
            }
            setCartState(cartState === 'expanded' ? 'closed' : 'expanded')
            sendShopifyAnalytics({
              eventName: AnalyticsEventName.ADD_TO_CART,
              payload: {
                ...getClientBrowserParameters(),
                shopId: 'gid://shopify/Shop/23806877',
                hasUserConsent: true,
                cartId: cartId as string,
                currency: 'USD',
                totalValue: parseFloat(selectedVariant.priceV2.amount),
                products: [
                  {
                    productGid: product.id,
                    name: product.title,
                    brand: 'CHNGE',
                    price: selectedVariant.priceV2.amount,
                    quantity: 1,
                    variantGid: selectedVariant.id,
                    variantName: selectedVariant.title,
                  },
                ],
              },
            })
            setTimeout(() => {
              setCartState('closed')
            }, 3000)
          }}
          className={`w-full ${
            status === 'updating' ? 'cursor-wait' : 'cursor-pointer'
          }`}
        >
          <div className="flex w-full justify-between rounded-8 bg-yellow px-[24px] py-[28px] uppercase text-darkGray md:py-[38px]">
            <div className="flex gap-4">
              {selectedVariant.compareAtPrice ? (
                <Money
                  data={selectedVariant.compareAtPrice as any}
                  className="line-through opacity-50"
                  withoutTrailingZeros
                />
              ) : null}
              <Money
                data={selectedVariant.priceV2 as any}
                withoutTrailingZeros
              />
            </div>
            <div>{addToCartText}</div>
          </div>
        </AddToCartButton>
      ) : (
        <button
          onClick={() => setNotifyId(selectedVariant.id)}
          className="flex w-full cursor-pointer justify-between rounded-8 bg-yellow px-[24px] py-[28px] uppercase text-darkGray md:py-[38px]"
        >
          <div>
            <Money data={selectedVariant.priceV2 as any} withoutTrailingZeros />
          </div>
          <div>Notify Me</div>
        </button>
      )}
    </div>
  )
}
